<template>
  <div>
    <!-- Filter Section -->
    <div class="mb-1">
      <div class="relative w-full">
        <select
            v-model="selectedFilter"
            class="appearance-none w-full bg-transparent text-primary text-base rounded-md pl-0 pr-8 py-2 focus:outline-none cursor-pointer"
        >
          <option value="all">All MOVEMENTS</option>
          <option value="last30">LAST 30 MOVEMENTS</option>
          <option value="last20">LAST 20 MOVEMENTS</option>
        </select>
        <component :is="ChevronDown"
                   class="pointer-events-none absolute inset-y-0 top-2 right-2 w-6 h-6 text-primary"/>
      </div>
    </div>

    <!-- Table -->
    <div class="mx-auto">
      <div class="overflow-x-auto bg-white p-4 rounded-lg border border-gray-300">
        <table class="min-w-full divide-y divide-gray-200 ">
          <thead class="bg-white">
          <tr>
            <th class="px-4 py-2 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">Oper. Date</th>
            <th class="px-4 py-2 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">Value Date</th>
            <th class="px-4 py-2 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">Doc. No.</th>
            <th class="px-4 py-2 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">Description</th>
            <th class="px-4 py-2 text-right text-xs font-bold text-gray-900 uppercase tracking-wider">Value</th>
            <th class="px-4 py-2 text-right text-xs font-bold text-gray-900 uppercase tracking-wider">Balance</th>
            <th class="relative px-4 py-2"><span class="sr-only">Actions</span></th>
          </tr>
          </thead>

          <tbody class="bg-white divide-y divide-gray-200">
          <tr v-for="(item, idx) in filteredMovements" :key="idx" class="hover:bg-gray-50">
            <td class="px-4 py-2 whitespace-nowrap text-sm text-gray-700">{{ item.oper_date }}</td>
            <td class="px-4 py-2 whitespace-nowrap text-sm text-gray-700">{{ item.value_date }}</td>
            <td class="px-4 py-2 whitespace-nowrap text-sm text-gray-700">{{ item.doc_no }}</td>
            <td class="px-4 py-2 whitespace-nowrap text-sm text-gray-700">{{ item.description }}</td>
            <td
                :class="item.value.includes('-') ? 'text-red-600' : 'text-green-600'"
                class="px-4 py-2 whitespace-nowrap text-sm font-semibold text-right"
            >
              {{ item.value }}
            </td>
            <td class="px-4 py-2 whitespace-nowrap text-sm text-gray-700 text-right">{{ item.balance }}</td>
            <td class="px-4 py-2 whitespace-nowrap text-right text-gray-700 cursor-pointer hover:text-orange-600">
              <component :is="FilePlus" class="w-5 h-5 mb-2"
                         @click="openModal(item)"/>
            </td>
          </tr>

          <!-- اگر حرکت مالی وجود نداشت -->
          <tr v-if="filteredMovements.length === 0">
            <td colspan="7" class="text-center py-4 text-gray-500">No movements available.</td>
          </tr>

          </tbody>
        </table>
      </div>

      <div class="flex justify-center gap-x-2 mt-6">
<!--        <button type="button" class="flex items-center justify-center bg-primary font-medium text-white px-8 py-3 rounded-lg shadow">-->
<!--          ALLOW HISTORY ACCESS-->
<!--        </button>-->
        <button  @click="exportToExcel" type="submit" class="flex items-center justify-center bg-primary font-medium text-white px-8 py-3 rounded-lg shadow">
          VIEW IN EXCEL
        </button>
        <button @click="exportToPDF" type="submit" class="flex items-center justify-center bg-primary font-medium text-white px-8 py-3 rounded-lg shadow">
          DOWNLOAD PDF
        </button>
      </div>
    </div>

    <!-- Modal -->
    <TransactionModal
        :isOpen="isModalOpen"
        :transaction="selectedTransaction"
        @close="isModalOpen = false"
    />
  </div>
</template>

<script setup>
import { ref, watch,computed, defineProps } from 'vue'
import { ChevronDown, FilePlus } from 'lucide-vue-next'
import TransactionModal from "@/components/view/TransactionModal.vue"
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';


const props = defineProps({
  account: Object
})

const selectedFilter = ref('all')
const movements = ref([])

const isModalOpen = ref(false)
const selectedTransaction = ref({})

function openModal(transaction) {
  selectedTransaction.value = transaction
  isModalOpen.value = true
}

// وقتی props.account تغییر کرد، حرکت‌ها رو به روز می‌کنیم
watch(() => props.account, (newAccount) => {
  if (newAccount?.movements) {
    movements.value = newAccount.movements.map(m => ({
      oper_date: m.oper_date,
      value_date: m.value_date,
      doc_no: m.doc_no,
      description: m.description,
      value: m.value,
      balance: m.balance
    }))
  } else {
    movements.value = []
  }
}, { immediate: true })

// فیلتر کردن بر اساس انتخاب
const filteredMovements = computed(() => {
  if (selectedFilter.value === 'last30') return movements.value.slice(0,30)
  if (selectedFilter.value === 'last20') return movements.value.slice(0,20)
  return movements.value
})


function exportToExcel() {
  if (!movements.value.length || !props.account) return;

  const wb = XLSX.utils.book_new();

  // -------------------------
  // 1️⃣ Sheet ایجاد
  // -------------------------
  const ws = XLSX.utils.aoa_to_sheet([]);

  // -------------------------
  // 2️⃣ اطلاعات حساب (2 ستونه) با رنگ
  // -------------------------
  const accountInfo = [
    ['Account Name', props.account.account_name],
    ['Account Number', props.account.account_number],
    ['Accounting Balance', props.account.accounting_balance],
    ['Available Balance', props.account.available_balance]
  ];

  XLSX.utils.sheet_add_aoa(ws, accountInfo, { origin: 0 });

  // استایل برای ردیف های اطلاعات حساب
  accountInfo.forEach((row, i) => {
    const cellKey = `A${i + 1}`;
    const cellValue = `B${i + 1}`;

    ws[cellKey].s = {
      font: { bold: true, color: { rgb: 'FFFFFF' } },
      fill: { fgColor: { rgb: '4F46E5' } }, // رنگ بنفش
      alignment: { vertical: 'center', horizontal: 'center' }
    };

    ws[cellValue].s = {
      font: { bold: false, color: { rgb: '000000' } },
      fill: { fgColor: { rgb: 'E0E7FF' } }, // رنگ روشن بنفش
      alignment: { vertical: 'center', horizontal: 'center' }
    };
  });

  // یک ردیف خالی بعد از info
  XLSX.utils.sheet_add_aoa(ws, [[]], { origin: accountInfo.length });

  // -------------------------
  // 3️⃣ جدول Movements
  // -------------------------
  const movementHeader = ['Oper. Date', 'Value Date', 'Doc. No.', 'Description', 'Value', 'Balance'];
  XLSX.utils.sheet_add_aoa(ws, [movementHeader], { origin: accountInfo.length + 1 });

  const movementsData = movements.value.map(m => [
    m.oper_date,
    m.value_date,
    m.doc_no,
    m.description,
    m.value,
    m.balance
  ]);

  XLSX.utils.sheet_add_aoa(ws, movementsData, { origin: accountInfo.length + 2 });

  // استایل برای هدر Movements
  movementHeader.forEach((_, i) => {
    const cell = XLSX.utils.encode_cell({ r: accountInfo.length + 1, c: i });
    if (!ws[cell]) return;
    ws[cell].s = {
      font: { bold: true, color: { rgb: 'FFFFFF' } },
      fill: { fgColor: { rgb: '1F2937' } }, // خاکستری تیره
      alignment: { vertical: 'center', horizontal: 'center' }
    };
  });

  // رنگ Value قرمز یا سبز
  movementsData.forEach((row, rIdx) => {
    const valueCell = XLSX.utils.encode_cell({ r: accountInfo.length + 2 + rIdx, c: 4 });
    if (!ws[valueCell]) return;
    ws[valueCell].s = {
      font: { color: { rgb: row[4].includes('-') ? 'DC2626' : '16A34A' }, bold: true },
      alignment: { horizontal: 'right' }
    };
    // راست‌چین کردن Balance
    const balanceCell = XLSX.utils.encode_cell({ r: accountInfo.length + 2 + rIdx, c: 5 });
    if (ws[balanceCell]) ws[balanceCell].s = { alignment: { horizontal: 'right' } };
  });

  XLSX.utils.book_append_sheet(wb, ws, 'Movements');

  // -------------------------
  // 4️⃣ خروجی
  // -------------------------
  const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array', cellStyles: true });
  saveAs(new Blob([wbout], { type: 'application/octet-stream' }), 'movements.xlsx');
}



function exportToPDF() {
  if (!movements.value.length || !props.account) return;

  const doc = new jsPDF();

  // ---------------------------
  // 1️⃣ اطلاعات حساب (2 ستونه)
  // ---------------------------
  const accountInfo = [
    ['Account Name', props.account.account_name],
    ['Account Number', props.account.account_number],
    ['Accounting Balance', props.account.accounting_balance],
    ['Available Balance', props.account.available_balance]
  ];

  autoTable(doc, {
    head: [['Key', 'Value']],
    body: accountInfo,
    startY: 10,
    theme: 'grid',
    headStyles: { fillColor: [79, 70, 229], textColor: 255, halign: 'center' },
    bodyStyles: { fillColor: [224, 231, 255], textColor: 0 },
    styles: { fontSize: 11 }
  });

  // ---------------------------
  // 2️⃣ Movements جدول
  // ---------------------------
  const movementsData = movements.value.map(m => [
    m.oper_date,
    m.value_date,
    m.doc_no,
    m.description,
    m.value,
    m.balance
  ]);

  const startY = doc.lastAutoTable.finalY + 10;

  autoTable(doc, {
    head: [['Oper. Date', 'Value Date', 'Doc. No.', 'Description', 'Value', 'Balance']],
    body: movementsData,
    startY,
    theme: 'grid',
    headStyles: { fillColor: [31, 41, 55], textColor: 255, halign: 'center' },
    bodyStyles: { fontSize: 10 },
    didParseCell: (data) => {
      if (data.column.index === 4 && data.cell.section === 'body') {
        data.cell.styles.textColor = data.cell.text[0].includes('-') ? [220, 38, 38] : [22, 163, 74];
        data.cell.styles.halign = 'right';
      }
      if (data.column.index === 5 && data.cell.section === 'body') {
        data.cell.styles.halign = 'right';
      }
    }
  });

  // ---------------------------
  // 3️⃣ ذخیره PDF
  // ---------------------------
  doc.save('movements.pdf');
}


</script>
